import { useMemo } from 'react'
import {
  TextV2 as Text,
  Box,
  Flex,
  Form,
  Loader,
  Colors,
  InputV2,
  Tag,
  Tooltip,
} from '@pinchenterprisesnpm/friday-ui'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown, faChevronUp } from '@fortawesome/pro-light-svg-icons'
import {
  faChevronDown as faChevronDownSolid,
  faChevronUp as faChevronUpSolid,
} from '@fortawesome/pro-regular-svg-icons'
import { useRosterMembersData } from '@src/features/shift-post-v2/steps/invite-upshifters-step/components/roster-section/hooks/useRosterData'
import { useInputFieldHacks } from '@src/features/shift-post-v2/steps/invite-upshifters-step/components/roster-section/hooks/useInputFieldHacks'
import { ACCEPTANCE_CRITERIA_OPTION } from '@src/features/shift-post-v2/steps/acceptance-criteria-step/acceptance-criteria-step.types'
import { connect } from 'formik'
import { getSelectedAcceptanceCriteriaOption } from '@src/features/shift-post-v2/utils'
import { ROSTER_INVITE_STATUS } from '@network/models/roster.model'
import { motion, AnimatePresence } from 'framer-motion'
import { numberToOrdinalWord } from '@src/helpers/numberHelpers'
import { RowOfAvatars } from '@src/components/business/RowOfAvatars'
import { BadgeItem } from 'components/business/BadgesCell/style'
import { UserListItem } from '../user-list-item'
import { IRosterSectionProps } from './roster-section.types'
import {
  BoldBlueSpan,
  StyledRosterSection,
  StyledRosterSectionContainer,
} from './roster-section.styles'

const { Checkbox } = Form

export const RosterSection = connect<IRosterSectionProps>(
  ({
    roster,
    isExpanded,
    onToggle,
    customOnSelect,
    customIsSelected,
    formik,
    containerStyle,
    invite_status,
    handleMoveRosterPosition,
    oneBasedIndex,
    isRosterPositionValid,
    isUnavailable,
    hideReordering,
  }) => {
    const {
      handleInputMouseDown,
      handleContainerClick,
      handleInputMouseUp,
      inputNumberKey,
      updateInputNumberKey,
    } = useInputFieldHacks(oneBasedIndex)

    // We must have an empty object if formik is not provided
    const { values, setFieldValue } = formik ?? {}

    const { isFetching, members } = useRosterMembersData(roster.id, isExpanded)

    const badgeProps = useMemo(() => {
      if (invite_status === undefined) {
        return null // Don't show badge if invite_status is undefined
      }

      if (invite_status === ROSTER_INVITE_STATUS.FINISHED) {
        return {
          text: 'Invited Before',
          type: 'warning_300' as const,
        }
      } else if (invite_status === ROSTER_INVITE_STATUS.PENDING) {
        return {
          text: 'Invitation in Progress',
          type: 'primary' as const,
        }
      }
      return null
    }, [invite_status])

    const isSelected =
      customIsSelected ?? values?.roster_ids?.includes(roster.id) ?? false

    const selectedAcceptanceCriteriaOption = useMemo(() => {
      return getSelectedAcceptanceCriteriaOption(
        values?.auto_accept_by_rosters,
        values?.auto_accept,
      )
    }, [values?.auto_accept_by_rosters, values?.auto_accept])

    const isSelectionDisabled =
      selectedAcceptanceCriteriaOption ===
        ACCEPTANCE_CRITERIA_OPTION.VERIFIED_FROM_ROSTERS || isUnavailable

    const onSelect =
      customOnSelect ??
      ((id: number) => {
        if (isSelected) {
          setFieldValue(
            'roster_ids',
            values?.roster_ids?.filter(rosterId => rosterId !== id) ?? [],
          )
        } else {
          setFieldValue('roster_ids', [...(values?.roster_ids ?? []), id])
        }
      })

    const moveToPosition = (oneBasedPosition: number) => {
      updateInputNumberKey()
      if (Number.isNaN(oneBasedPosition) || oneBasedPosition < 1) return

      // Convert 1-based UI input to 0-based index used internally
      const zeroBasedIndex = Math.max(1, oneBasedPosition) - 1

      if (!isRosterPositionValid(zeroBasedIndex)) return

      handleMoveRosterPosition(zeroBasedIndex)
    }

    const handleMoveToPositionChange = (
      e: React.ChangeEvent<HTMLInputElement>,
    ) => {
      const rawValue = Number(e.target.value)
      moveToPosition(rawValue)
    }

    const handleMoveUp = () => {
      moveToPosition(oneBasedIndex - 1)
    }

    const handleMoveDown = () => {
      moveToPosition(oneBasedIndex + 1)
    }

    return (
      <StyledRosterSection
        key={roster.id}
        style={containerStyle}
        isSelectionDisabled={isSelectionDisabled}
      >
        <StyledRosterSectionContainer
          onClick={() => handleContainerClick(() => onToggle(roster.id))}
          justifyContent=""
          alignItems="center"
        >
          {!hideReordering && (
            <Flex alignItems="center">
              <Flex alignItems="center">
                {!isSelected && <Box width="137px" height="40px" />}
                {isSelected && (
                  <>
                    <Text color="gray.900" mr="xs">
                      Move to
                    </Text>
                    <Box onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                      <InputV2
                        isDisabled={isSelectionDisabled}
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        onMouseDown={handleInputMouseDown}
                        onMouseUp={handleInputMouseUp}
                        rightElement={
                          <Flex direction="column" mt="-10px" mr="-5px">
                            <Box
                              px="xs"
                              pt="xs"
                              onClick={
                                isSelectionDisabled ? () => {} : handleMoveUp
                              }
                            >
                              <FontAwesomeIcon
                                icon={faChevronUpSolid}
                                color={Colors.muted}
                              />
                            </Box>
                            <Box
                              px="xs"
                              pb="xs"
                              onClick={
                                isSelectionDisabled ? () => {} : handleMoveDown
                              }
                            >
                              <FontAwesomeIcon
                                icon={faChevronDownSolid}
                                color={Colors.muted}
                              />
                            </Box>
                          </Flex>
                        }
                        name="positions"
                        width="80px"
                        min={1}
                        value={oneBasedIndex}
                        onChange={handleMoveToPositionChange}
                        key={inputNumberKey}
                      />
                    </Box>
                  </>
                )}
              </Flex>
            </Flex>
          )}
          <Flex alignItems="center">
            <Box
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              ml="md"
              mr="sm"
            >
              <Checkbox
                isDisabled={isSelectionDisabled}
                value={isSelected}
                onChange={() => onSelect(roster.id)}
                borderRadius="4px"
                borderWidth="2px"
                borderColor={Colors.muted}
                backgroundColor={Colors.white}
              />
            </Box>

            <Flex flexDirection="column">
              <Flex alignItems="center" mt="2px">
                <Text variant="h6">{roster.name}</Text>

                <Flex ml="xs">
                  {isUnavailable && (
                    <Tooltip text="None of the Upshifters in this roster are eligible for an invite.">
                      <span>
                        <Tag size="small" type="danger" mx="xs">
                          Unavailable
                        </Tag>
                      </span>
                    </Tooltip>
                  )}
                  {badgeProps && (
                    <BadgeItem type={badgeProps.type} mr="xs">
                      {badgeProps.text}
                    </BadgeItem>
                  )}
                </Flex>
              </Flex>

              {/* <Text color="gray">{roster.members_count} total</Text> */}
              <RowOfAvatars
                avatars={
                  roster.members?.data.map(member => member.avatar_thumb_url) ??
                  []
                }
                totalCount={roster.members_count ?? 0}
              />
              {/* Only show invitation order for selected rosters */}
              {isSelected && (
                <Text color="gray.900" mt="2px">
                  This Roster will be invited{' '}
                  <BoldBlueSpan>
                    {numberToOrdinalWord(oneBasedIndex)}.
                  </BoldBlueSpan>
                </Text>
              )}
            </Flex>
          </Flex>
          <Flex ml="auto" mr="sm" alignItems="center">
            <FontAwesomeIcon
              icon={isExpanded ? faChevronUp : faChevronDown}
              color="gray"
              size="lg"
            />
          </Flex>
        </StyledRosterSectionContainer>

        {isFetching && (
          <Flex justifyContent="center" alignItems="center">
            <Loader />
          </Flex>
        )}

        <AnimatePresence>
          {isExpanded && members && !isFetching && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            >
              <Box ml="md">
                {members.map(user => (
                  <UserListItem key={user.id} user={user} />
                ))}
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </StyledRosterSection>
    )
  },
)
